import {Debouncer} from 'external/dev_april_corgi+/js/common/debouncer';
import {
  Controller,
  Response,
} from 'external/dev_april_corgi+/js/corgi/controller';
import {EmptyDeps} from 'external/dev_april_corgi+/js/corgi/deps';
import {CorgiEvent, EventSpec} from 'external/dev_april_corgi+/js/corgi/events';

import * as starlark from './nodes/starlark';
import {ScriptType, ScriptValue} from './types/data';

interface Args {
  onUpdate: (value: ScriptValue) => void;
  type: ScriptType;
  value: ScriptValue;
}

export interface State {
  error: string | undefined;
}

export class ScriptEditorController extends Controller<
  Args,
  EmptyDeps,
  HTMLElement,
  State
> {
  private readonly debouncer: Debouncer;
  private readonly onUpdate: (value: ScriptValue) => void;
  private readonly type: ScriptType;
  private value: ScriptValue;

  constructor(response: Response<ScriptEditorController>) {
    super(response);
    this.onUpdate = response.args.onUpdate;
    this.type = response.args.type;
    this.value = response.args.value;

    const check = () => {
      const star = starlark.ready();
      if (!star.finished) {
        return;
      }
      try {
        star.value().evaluate(this.value.value, this.type);
        this.updateState({
          ...this.state,
          error: undefined,
        });
      } catch (e: unknown) {
        this.updateState({
          ...this.state,
          error: wrap(String(e), 80),
        });
      }
    };
    this.debouncer = new Debouncer(200, check);
    check();

    // We don't want corgi to preventDefault regular typing so we don't use it to process keydown
    const textarea = this.query()
      .children('textarea')
      .one()
      .element() as HTMLTextAreaElement;
    this.registerListener(textarea, 'keydown', e => {
      if (e.key !== 'Tab') {
        return;
      }

      e.preventDefault();
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const value = textarea.value;
      if (start === end) {
        if (e.shiftKey) {
          const before = value.substring(0, start).split('\n');
          const lines = value.split('\n');
          if (lines[before.length - 1].startsWith('  ')) {
            lines[before.length - 1] = lines[before.length - 1].substring(2);
            textarea.value = lines.join('\n');
            textarea.selectionStart = start - 2;
            textarea.selectionEnd = end - 2;
          }
        } else {
          textarea.value = `${value.substring(0, start)}  ${value.substring(end)}`;
          textarea.selectionStart = start + 2;
          textarea.selectionEnd = end + 2;
        }
      } else {
        const before = value.substring(0, start).split('\n');
        const after = value.substring(end).split('\n');
        const lines = value.split('\n');
        const inAfter = lines[lines.length - after.length] !== after[0];
        let firstAffected = false;
        let added = 0;
        for (
          let i = before.length - 1;
          i <= lines.length - after.length - (inAfter ? 0 : 1);
          ++i
        ) {
          if (!lines[i]) {
            continue;
          }

          if (e.shiftKey) {
            if (lines[i].startsWith('  ')) {
              firstAffected = true;
              lines[i] = lines[i].substring(2);
              added -= 2;
            }
          } else {
            firstAffected = true;
            lines[i] = '  ' + lines[i];
            added += 2;
          }
        }
        textarea.value = lines.join('\n');
        textarea.selectionStart =
          start + (firstAffected ? 0 : 2 * Math.sign(added));
        textarea.selectionEnd = end + added;
      }
    });
  }

  async changed(e: CorgiEvent<EventSpec<unknown>>): Promise<void> {
    this.value = {
      kind: 'script',
      value: (e.targetElement.element() as HTMLTextAreaElement).value,
    };
    this.debouncer.trigger();
    this.onUpdate(this.value);
  }
}

function wrap(s: string, columns: number): string {
  const out = [];
  for (const row of s.split('\n')) {
    for (let i = 0; i < row.length; i += columns) {
      out.push(row.substring(i, i + columns));
    }
  }
  return out.join('\n');
}
